import { ISelectItem, Pill, Tile, useSettings } from "@react-gcc-eds/core";
import cx from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { useMetricsFiltersActions } from "../../../../actions/metrics-filters-actions";
import { IExternalOrdersRedirectionFilters } from "../../../../actions/orders-filters-actions";
import { IExternalItemsRedirectionFilters } from "../../../../actions/po-items-filters-actions";
import { useSettingsActions } from "../../../../actions/settings-actions";
import { useStore } from "../../../../store";
import { dateAsYMDString } from "../../../../utils";
import { useLocalization } from "../../../../utils/localization";
import DatePicker from "../../../filters/date-picker";
import { usePredefinedRanges } from "../../../filters/date-picker/predefined-ranges";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";
import { StatusBarChart } from "../bars-chart";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { OpCoFilterDropDown, PurchaseOrdersFilterDropDown } from "./filters";

/**
 * The Dashboard "Overall order status" barchart tile.
 */
const OverallOrderStatusBars = ({
  labels,
  className,
  customerId,
  alignment,
  onItemSelected
}: {
  labels: { [key: string]: string | undefined };
  className: string;
  customerId: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const [state, _] = useStore();
  const [selectedTab, setSelectedTab] = useState<ITab>();
  const [tabs, setTabs] = useState<ITab[]>([]);
  const [actions, setActions] = useState<React.ReactElement<any, any>[]>([]);
  const tileRef = useRef<HTMLDivElement>(null);
  const { theme } = useSettings();
  const { currentCustomer } = useSettingsActions();
  const {
    orderCreationDateBarsMetric: orderCreationDateBarsMetricLoading,
    itemCreationDateBarsMetric: itemCreationDateBarsMetricLoading
  } = state.fetchingData.metrics;
  const { localeString } = useLocalization();
  const { rangeAsString, largestRange, isLargestRange } = usePredefinedRanges(
    new Date(currentCustomer().dataAvailableFromDate)
  );
  const {
    getOrderCreationDateBarsMetric,
    getItemCreationDateBarsMetric,
    getItemCreationPurchaseOrderNumbers
  } = useMetricsActions();
  const {
    setMetricsOverallOrderStatusBarsCreationRange,
    setMetricsOverallOrderStatusBarsOrderNumbers,
    setMetricsOverallOrderStatusBarsOpCos,
    setMetricsOverallOrderStatusBarsItemsOpCos
  } = useMetricsFiltersActions();

  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);

  useEffect(() => {
    if (state.metricsFilters.overallOrderStatusBarsCreationRange) {
      const {
        overallOrderStatusBarsCreationRange: { from, to },
        overallOrderStatusBarsOpCos
      } = state.metricsFilters;

      getOrderCreationDateBarsMetric(
        dateAsYMDString(from),
        dateAsYMDString(to),
        overallOrderStatusBarsOpCos ? overallOrderStatusBarsOpCos.map(opCo => Number(opCo.id)) : []
      );
    }
  }, [
    state.metricsFilters.overallOrderStatusBarsCreationRange,
    state.metricsFilters.overallOrderStatusBarsOpCos,
    customerId
  ]);

  useEffect(() => {
    if (state.metricsFilters.overallOrderStatusBarsCreationRange) {
      const {
        overallOrderStatusBarsCreationRange: { from, to },
        overallOrderStatusBarsItemsOpCos,
        overallOrderStatusBarsItemsOrderNumbers
      } = state.metricsFilters;

      getItemCreationDateBarsMetric(
        dateAsYMDString(from),
        dateAsYMDString(to),
        overallOrderStatusBarsItemsOrderNumbers ? overallOrderStatusBarsItemsOrderNumbers : [],
        overallOrderStatusBarsItemsOpCos
          ? overallOrderStatusBarsItemsOpCos.map(opCo => Number(opCo.id))
          : []
      );
    }
  }, [
    state.metricsFilters.overallOrderStatusBarsCreationRange,
    state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers,
    state.metricsFilters.overallOrderStatusBarsItemsOpCos,
    customerId
  ]);

  useEffect(() => {
    if (state.metricsFilters.overallOrderStatusBarsCreationRange) {
      const {
        overallOrderStatusBarsCreationRange: { from, to }
      } = state.metricsFilters;

      const selectedOpCos = state.metricsFilters.overallOrderStatusBarsItemsOpCos?.map(opCo =>
        Number(opCo.id)
      );

      getItemCreationPurchaseOrderNumbers(
        dateAsYMDString(from),
        dateAsYMDString(to),
        selectedOpCos ?? []
      );
    }
  }, [
    state.metricsFilters.overallOrderStatusBarsCreationRange,
    state.metricsFilters.overallOrderStatusBarsItemsOpCos
  ]);

  useEffect(() => {
    const tabs = [];

    if (labels["purchaseOrders"] !== undefined) {
      tabs.push(purchaseOrderTab);
    }
    if (labels["purchaseOrderItems"] !== undefined) {
      tabs.push(purchaseOrderItemsTab);
    }
    if (tabs.length) {
      setTabs(tabs);
      if (!selectedTab) {
        setSelectedTab(tabs[0]);
      }
    }
  }, [orderCreationDateBarsMetricLoading, itemCreationDateBarsMetricLoading]);

  useEffect(() => {
    const myActions = [];
    let opCoDropdown;

    switch (selectedTab?.title) {
      case labels["purchaseOrders"]:
        myActions.push(dateFilter);

        if (state.customer?.isHoldingCompany) {
          opCoDropdown = (
            <OpCoFilterDropDown
              tab={"order"}
              onChange={setMetricsOverallOrderStatusBarsOpCos}
              selectedItems={getSelectedOpCos("order")}
              key={"opco-filter"}
            />
          );
          myActions.push(opCoDropdown);
        }
        break;
      case labels["purchaseOrderItems"]:
        myActions.push(dateFilter);
        myActions.push(
          <PurchaseOrdersFilterDropDown
            onChange={setMetricsOverallOrderStatusBarsOrderNumbers}
            selectedItems={getSelectedOrders()}
            key={"orders-filter"}
          />
        );

        if (state.customer?.isHoldingCompany) {
          opCoDropdown = (
            <OpCoFilterDropDown
              tab={"item"}
              onChange={setMetricsOverallOrderStatusBarsItemsOpCos}
              selectedItems={getSelectedOpCos("item")}
              key={"opco-filter"}
            />
          );
          myActions.push(opCoDropdown);
        }
        break;
      default:
        return;
    }

    setActions(myActions);
  }, [
    selectedTab,
    state.metrics.itemCreationDateBarsAvailablePurchaseOrderNumbers,
    state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers,
    state.metricsFilters.overallOrderStatusBarsOpCos,
    state.metricsFilters.overallOrderStatusBarsItemsOpCos
  ]);

  const PurchaseOrderBarChart = useCallback(() => {
    const { orderCreationDateBarsMetric } = state.metrics;

    return (
      <StatusBarChart
        metrics={orderCreationDateBarsMetric}
        loading={orderCreationDateBarsMetricLoading === true}
        unit={""}
        datesRange={state.metricsFilters.overallOrderStatusBarsCreationRange}
        onSelection={(filters: IExternalOrdersRedirectionFilters) =>
          onItemSelected &&
          onItemSelected({
            graph: DashboardGraph.Orders,
            filters: {
              ...filters,
              opCos: state.metricsFilters.overallOrderStatusBarsOpCos
                ? state.metricsFilters.overallOrderStatusBarsOpCos.map(opCo => opCo.title)
                : undefined
            }
          })
        }
      />
    );
  }, [
    orderCreationDateBarsMetricLoading,
    state.metrics.orderCreationDateBarsMetric,
    state.metricsFilters.overallOrderStatusBarsCreationRange
  ]);

  const PurchaseOrdersItemsBarChart = useCallback(() => {
    const { itemCreationDateBarsMetric } = state.metrics;

    return (
      <StatusBarChart
        metrics={itemCreationDateBarsMetric}
        loading={itemCreationDateBarsMetricLoading === true}
        unit={""}
        datesRange={state.metricsFilters.overallOrderStatusBarsCreationRange}
        onSelection={(barFilters: IExternalItemsRedirectionFilters) => {
          if (onItemSelected) {
            onItemSelected({
              graph: DashboardGraph.Items,
              filters: {
                ...barFilters,
                opCos: state.metricsFilters.overallOrderStatusBarsItemsOpCos
                  ? state.metricsFilters.overallOrderStatusBarsItemsOpCos.map(opCo => opCo.title)
                  : undefined,
                purchaseOrderNumber: state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers
              }
            });
          }
        }}
      />
    );
  }, [
    itemCreationDateBarsMetricLoading,
    state.metrics.itemCreationDateBarsMetric,
    state.metricsFilters.overallOrderStatusBarsCreationRange
  ]);

  const resetFilter = () => {
    setMetricsOverallOrderStatusBarsCreationRange(largestRange.selector());
  };

  const resetFilterPill = () => {
    return (
      <Pill className={cx("filter-pill", theme)} onToggle={resetFilter}>
        Reset filters
      </Pill>
    );
  };

  const filtersSummary = (
    <div className="dashboard-active-filter">
      <Pill>{rangeAsString(state.metricsFilters.overallOrderStatusBarsCreationRange)}</Pill>
      {!isLargestRange(state.metricsFilters.overallOrderStatusBarsCreationRange)
        ? resetFilterPill()
        : null}
    </div>
  );

  const purchaseOrderTab = {
    view: PurchaseOrderBarChart(),
    title: labels["purchaseOrders"],
    index: 0
  } as ITab;

  const purchaseOrderItemsTab = {
    view: PurchaseOrdersItemsBarChart(),
    title: labels["purchaseOrderItems"],
    index: 1
  } as ITab;

  const dateFilter = (
    <DatePicker
      dual={state.userPreferences.useDualCalendar}
      rolling
      selectedRange={state.metricsFilters.overallOrderStatusBarsCreationRange}
      oldestDate={new Date(currentCustomer().dataAvailableFromDate)}
      onRangeSelected={setMetricsOverallOrderStatusBarsCreationRange}
      key="date-picker"
      className="filter"
    />
  );

  const getSelectedOrders = () => {
    const items = state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers
      ? state.metricsFilters.overallOrderStatusBarsItemsOrderNumbers.map(
          po => ({ value: po, title: po }) as ISelectItem
        )
      : [];
    return items;
  };

  const getSelectedOpCos = (tab: "order" | "item") => {
    let items;

    if (tab === "item") {
      items = state.metricsFilters.overallOrderStatusBarsItemsOpCos
        ? state.metricsFilters.overallOrderStatusBarsItemsOpCos.map(
            opCo => ({ value: opCo.id, title: opCo.title }) as ISelectItem
          )
        : [];
    } else {
      items = state.metricsFilters.overallOrderStatusBarsOpCos
        ? state.metricsFilters.overallOrderStatusBarsOpCos.map(
            opCo => ({ value: opCo.id, title: opCo.title }) as ISelectItem
          )
        : [];
    }
    return items;
  };

  const tileHeader = (
    <div className="dashboard-tile">
      <DashboardTooltipHeader
        tooltipInfo="Graph is showing the status for the selected time period"
        alignment={alignment}
        header={localeString("Dashboard_OverallOrderStatusBarChart") || "Overall order status"}
      />
    </div>
  );

  return (
    <>
      <Tile
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
        subtitle={filtersSummary}
        actions={actions}
      >
        <CustomTabs onTabChange={(tab: ITab) => setSelectedTab(tab)} tabs={tabs} />
      </Tile>
    </>
  );
};

export default OverallOrderStatusBars;
